import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { LocationViewModel } from "../../../../shared/models/locations/LocationViewModel.model";
import { LocationNoteViewModel, LocationNoteStatus } from "../../../../shared/models/locations/LocationNoteViewModel.model";
import { LocationsService } from "../../../shared/locations.service";
import { FileUploadService } from "../../../../shared/services/files/fileUpload.service";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "app-location-notes-tab",
  templateUrl: "./location-notes-tab.component.html",
  styleUrls: ["./location-notes-tab.component.css"],
})
export class LocationNotesTabComponent implements OnInit, OnChanges {
  @Input() location: LocationViewModel;
  @Input() isEditMode: boolean;
  @Output() hasChanges = new EventEmitter<boolean>();

  notes: LocationNoteViewModel[] = [];
  filteredNotes: LocationNoteViewModel[] = [];
  loadingNotes: boolean = false;
  showAddModal: boolean = false;
  showEditModal: boolean = false;
  newNote: LocationNoteViewModel = this.createEmptyNote();
  editingNote: LocationNoteViewModel | null = null;
  searchText: string = "";
  filterStatus: string = "";
  filterDateFrom: string = "";
  filterDateTo: string = "";

  // Image attachment properties
  selectedFiles: File[] = [];
  showImageModal: boolean = false;
  currentImageGallery: any[] = [];
  currentImageIndex: number = 0;
  @ViewChild('addFileInput') addFileInput: ElementRef;
  @ViewChild('editFileInput') editFileInput: ElementRef;
  private filePreviewUrls: Map<File, string> = new Map();

  noteStatuses: string[] = ["Open", "Resolved"];
  statusOptions: any[] = [
    { label: "Open", value: LocationNoteStatus.Open },
    { label: "Resolved", value: LocationNoteStatus.Resolved }
  ];

  LocationNoteStatus = LocationNoteStatus;

  constructor(
    private locationsService: LocationsService,
    private fileUploadService: FileUploadService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    if (this.location?.id) {
      this.loadNotes();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["location"] && !changes["location"].firstChange && this.location?.id) {
      this.loadNotes();
    }
  }

  createEmptyNote(): LocationNoteViewModel {
    return {
      locationId: this.location?.id || "",
      note: "",
      status: LocationNoteStatus.Open,
      dateCreatedUtc: new Date(),
    };
  }

  loadNotes() {
    if (!this.location?.id) {
      return;
    }

    this.loadingNotes = true;
    this.locationsService.getLocationNotes(this.location.id).subscribe({
      next: (notes) => {
        this.notes = (notes || []).map((note: any) => ({
          ...note,
          dateCreatedUtc: note.dateCreatedUtc ? new Date(note.dateCreatedUtc) : undefined,
        }));
        // Sort by creation date descending (newest first)
        this.notes.sort(
          (a, b) =>
            new Date(b.dateCreatedUtc || 0).getTime() - new Date(a.dateCreatedUtc || 0).getTime()
        );
        this.filterNotes();
        this.loadingNotes = false;
      },
      error: (error) => {
        this.toastr.error("Error loading notes", "Error");
        this.loadingNotes = false;
        this.notes = [];
        this.filteredNotes = [];
      },
    });
  }

  filterNotes() {
    let filtered = [...this.notes];

    // Filter by status
    if (this.filterStatus) {
      const statusValue = this.filterStatus === "Open" ? LocationNoteStatus.Open : LocationNoteStatus.Resolved;
      filtered = filtered.filter((note) => note.status === statusValue);
    }

    // Filter by search text
    if (this.searchText) {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(
        (note) =>
          note.note.toLowerCase().includes(searchLower) ||
          this.getStatusText(note.status).toLowerCase().includes(searchLower) ||
          (note.createdByUserName || "").toLowerCase().includes(searchLower)
      );
    }

    // Filter by date range
    if (this.filterDateFrom || this.filterDateTo) {
      filtered = filtered.filter((note) => {
        const noteDate = note.dateCreatedUtc ? new Date(note.dateCreatedUtc) : null;
        if (!noteDate) return false;

        // Get local date string (YYYY-MM-DD) for comparison
        const noteDateStr = noteDate.toLocaleDateString('en-CA'); // Returns YYYY-MM-DD format
        if (this.filterDateFrom && noteDateStr < this.filterDateFrom) {
          return false;
        }
        if (this.filterDateTo && noteDateStr > this.filterDateTo) {
          return false;
        }
        return true;
      });
    }

    this.filteredNotes = filtered;
  }

  openAddModal() {
    this.newNote = this.createEmptyNote();
    this.selectedFiles = [];
    this.showAddModal = true;
  }

  closeAddModal() {
    this.showAddModal = false;
    this.newNote = this.createEmptyNote();
    this.cleanupFilePreviewUrls();
    this.selectedFiles = [];
    if (this.addFileInput) {
      this.addFileInput.nativeElement.value = '';
    }
  }

  openEditModal(note: LocationNoteViewModel) {
    this.editingNote = { ...note };
    this.selectedFiles = [];
    this.showEditModal = true;
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editingNote = null;
    this.cleanupFilePreviewUrls();
    this.selectedFiles = [];
    if (this.editFileInput) {
      this.editFileInput.nativeElement.value = '';
    }
  }

  saveNote() {
    if (!this.validateNote(this.newNote)) return;

    const noteToSave: any = {
      locationId: this.newNote.locationId,
      note: this.newNote.note.trim(),
      status: this.newNote.status,
    };

    // Upload images first if any are selected
    if (this.selectedFiles.length > 0) {
      this.fileUploadService.uploadFiles(this.selectedFiles, 'locationnotes').subscribe({
        next: (uploadResults: any[]) => {
          noteToSave.attachedImages = uploadResults.map(result => ({
            name: result.name,
            originalName: result.originalName || result.name,
            type: result.type,
            blobName: result.path
          }));
          this.createNoteWithData(noteToSave);
        },
        error: (error) => {
          this.toastr.error('Error uploading images');
          console.error(error);
        }
      });
    } else {
      this.createNoteWithData(noteToSave);
    }
  }

  private createNoteWithData(noteToSave: any) {
    this.locationsService.createLocationNote(noteToSave).subscribe({
      next: (savedNote: any) => {
        this.notes.unshift({
          ...savedNote,
          dateCreatedUtc: savedNote.dateCreatedUtc ? new Date(savedNote.dateCreatedUtc) : undefined,
        });
        this.filterNotes();
        this.toastr.success("Note saved successfully");
        this.closeAddModal();
        this.hasChanges.emit(true);
      },
      error: (error) => {
        this.toastr.error("Error saving note");
        console.error(error);
      },
    });
  }

  updateNote() {
    if (!this.editingNote || !this.validateNote(this.editingNote)) return;

    const noteToUpdate: any = {
      id: this.editingNote.id,
      locationId: this.editingNote.locationId,
      note: this.editingNote.note.trim(),
      status: this.editingNote.status,
      attachedImages: this.editingNote.attachedImages || []
    };

    // Upload new images if any are selected
    if (this.selectedFiles.length > 0) {
      this.fileUploadService.uploadFiles(this.selectedFiles, 'locationNotes').subscribe({
        next: (uploadResults: any[]) => {
          const newImages = uploadResults.map(result => ({
            name: result.name,
            originalName: result.originalName || result.name,
            type: result.type,
            blobName: result.path
          }));
          noteToUpdate.attachedImages = [...noteToUpdate.attachedImages, ...newImages];
          this.updateNoteWithData(noteToUpdate);
        },
        error: (error) => {
          this.toastr.error('Error uploading images');
          console.error(error);
        }
      });
    } else {
      this.updateNoteWithData(noteToUpdate);
    }
  }

  private updateNoteWithData(noteToUpdate: any) {
    this.locationsService.updateLocationNote(noteToUpdate).subscribe({
      next: (updatedNote: any) => {
        const index = this.notes.findIndex((n) => n.id === updatedNote.id);
        if (index !== -1) {
          this.notes[index] = {
            ...updatedNote,
            dateCreatedUtc: updatedNote.dateCreatedUtc ? new Date(updatedNote.dateCreatedUtc) : undefined,
          };
          this.filterNotes();
        }
        this.toastr.success("Note updated successfully");
        this.closeEditModal();
        this.hasChanges.emit(true);
      },
      error: (error) => {
        this.toastr.error("Error updating note");
        console.error(error);
      },
    });
  }

  deleteNote(note: LocationNoteViewModel) {
    if (!confirm("Are you sure you want to delete this note?")) return;

    this.locationsService.deleteLocationNote(note.id!).subscribe({
      next: () => {
        this.notes = this.notes.filter((n) => n.id !== note.id);
        this.filterNotes();
        this.toastr.success("Note deleted successfully");
        this.hasChanges.emit(true);
      },
      error: (error) => {
        this.toastr.error("Error deleting note");
        console.error(error);
      },
    });
  }

  validateNote(note: LocationNoteViewModel): boolean {
    if (note.status === undefined || note.status === null) {
      this.toastr.warning("Note status is required");
      return false;
    }
    if (!note.note || note.note.trim() === "") {
      this.toastr.warning("Note content is required");
      return false;
    }
    return true;
  }

  getStatusText(status: LocationNoteStatus): string {
    return status === LocationNoteStatus.Resolved ? "Resolved" : "Open";
  }

  getStatusClass(status: LocationNoteStatus): string {
    switch (status) {
      case LocationNoteStatus.Open:
        return "status-open";
      case LocationNoteStatus.Resolved:
        return "status-resolved";
      default:
        return "";
    }
  }

  getHighlightedText(text: string): string {
    if (!this.searchText || !text) {
      return text || "";
    }

    const re = new RegExp(this.searchText, "gi");
    return text.replace(re, "<mark>$&</mark>");
  }

  // Image attachment methods
  onImagesSelected(event: any, mode: 'add' | 'edit') {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Validate image files
    const validFiles: File[] = [];
    for (let file of Array.from(files) as File[]) {
      if (file.type.startsWith('image/')) {
        validFiles.push(file);
      } else {
        this.toastr.warning(`${file.name} is not an image file`, 'Invalid File');
      }
    }

    this.selectedFiles = [...this.selectedFiles, ...validFiles];
  }

  removeSelectedFile(index: number) {
    const file = this.selectedFiles[index];
    // Clean up the blob URL for this file
    const url = this.filePreviewUrls.get(file);
    if (url) {
      URL.revokeObjectURL(url);
      this.filePreviewUrls.delete(file);
    }
    this.selectedFiles.splice(index, 1);
  }

  removeExistingImage(index: number) {
    if (this.editingNote && this.editingNote.attachedImages) {
      this.editingNote.attachedImages.splice(index, 1);
    }
  }

  getImageUrl(fileName: string): string {
    return this.fileUploadService.getAzureFileUrl('locationnotes', fileName);
  }

  openImageModal(image: any, gallery: any[]) {
    this.currentImageGallery = gallery;
    this.currentImageIndex = gallery.findIndex(img => img.name === image.name);
    this.showImageModal = true;
  }

  closeImageModal() {
    this.showImageModal = false;
    this.currentImageGallery = [];
    this.currentImageIndex = 0;
  }

  previousImage() {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--;
    }
  }

  nextImage() {
    if (this.currentImageIndex < this.currentImageGallery.length - 1) {
      this.currentImageIndex++;
    }
  }

  getFilePreviewUrl(file: File): string {
    // Check if we already have a cached URL for this file
    if (!this.filePreviewUrls.has(file)) {
      const url = URL.createObjectURL(file);
      this.filePreviewUrls.set(file, url);
    }
    return this.filePreviewUrls.get(file)!;
  }

  private cleanupFilePreviewUrls() {
    // Revoke all blob URLs to prevent memory leaks
    this.filePreviewUrls.forEach(url => URL.revokeObjectURL(url));
    this.filePreviewUrls.clear();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}

